import { useEffect, useState } from "react";
import { deleteSource, updateSources } from "../../../../scripts/source";
import CheckboxField from "../../fields/CheckBoxField";
import NameField from "../../fields/NameField";
import NumberField from "../../fields/NumberField";
import ProcessorChoiceField from "../../fields/ProcessorChoiceField";
import StringField from "../../fields/StringField";
import { SourceIcon } from "../../styling/Icons";
import AddObjectButton from "../../table/AddObjectButton";
import { Placeholder } from "../../table/styles/TableStyles";
import { getDefaultId } from "../../table/tableUtils";
import { isHubVersionAtLeast } from "../hub/hubUtils";
import { addNewSource, getNextAddress } from "./sourceUtils";

const idField = 'sourceId';
const FLEX_MATRIX_MINIMUM_HUB_VERSION = '10.3.0';

export const getSourceTableProps = ({ sources, processors, hubs, customerId }) => {

    const getColumns = (sources) => [{
        key: 'name',
        displayName: 'Name',
        form: true,
        width: '150px',
        formValidate: (source) => {
            return source.name ? null : { type: 'ERROR', message: 'Name is required' };
        },
        render: (source, updateSource, cellControl) => (
            <NameField
                object={source}
                updateObject={updateSource}
                objects={sources}
                idField={'sourceId'}
                nameField={'name'}
                cellState={cellControl.cellState}
            />
        )
    },{
        key: 'processorId',
        displayName: 'Processor',
        width: '150px',
        form: true,
        formValidate: (source) => {
            return source.processorId ? null : { type: 'ERROR', message: 'Processor is required' };
        },
        getSuggested: () => getDefaultId(processors, 'processorId'),
        render: (source, updateSource, cellControl) => (
            <ProcessorChoiceField
                object={source}
                processors={processors}
                updateObject={(updatedProps) => updateSource({
                    ...updatedProps,
                    ...(!processorSupportsMultiInput(updatedProps.processorId, processors, hubs) ? { inputs: [] } : {})
                })}
                cellControl={cellControl}
            />
        )
    }, {
        key: 'address',
        displayName: 'Primary Input',
        width: '100px',
        form: true,
        formValidate: (source) => {
            return (source.address || source.address === 0) ? null : { type: 'ERROR', message: 'Address is required' };
        },
        getSuggested: () => getNextAddress(sources, processors),
        render: (source, updateSource, cellControl) => (
            <NumberField
                value={source.address}
                setValue={(value) => updateSource({ address: value })}
                cellState={cellControl.cellState}
            />
        )
    }, {
        key: 'inputs',
        displayName: hasMultiInputSourceSupport(processors, hubs)? 'Inputs' : '',
        width: '120px',
        form: true,
        hide: !hasMultiInputSourceSupport(processors, hubs),
        hideInItem: (source) => !showMultiInputField(source, processors, hubs),
        render: (source, updateSource, cellControl) => {
            if (showMultiInputField(source, processors, hubs)) {
                return (
                    <InputsField
                        inputs={source.inputs}
                        setInputs={(value) => updateSource({ inputs: value })}
                        cellState={cellControl.cellState}
                        disabled={!showMultiInputField(source, processors, hubs)}
                    />
                )
            } else {
                return (
                    <StringField
                        value=''
                        setValue={() => { }}
                        placeholder='Not supported by selected processor'
                        cellState={cellControl.cellState}
                    />
                )
            }
        }   
    }, {
        key: 'regulate',
        displayName: 'Automate',
        width: '60px',
        form: true,
        getSuggested: () => { return true },
        render: (source, updateSource, cellControl) => (
            <CheckboxField
                checked={source.regulate}
                setValue={() => updateSource({ regulate: +(!source.regulate) })}
                cellControl={cellControl}
            />
        )
    }];

    const saveObjects = async (changedSources) => {
        try {
            await updateSources(changedSources);
        } catch (err) {
            console.log('Failed to update sources');
        }
    }

    const deleteObject = async (source) => {
        try {
            await deleteSource(source.sourceId);
        } catch (err) {
            console.log('Error failed to delete source');
        }
    }

    const addObject = processors.length ? (newSource) => addNewSource(newSource, processors, customerId) : null;

    const getListItemProps = (source) => {
        return {
            displayName: source.name,
            Icon: SourceIcon
        }
    }
    
    const getPlaceholder = () => {
        return (
            <Placeholder>
                <p>No sources added.</p>
                <AddObjectButton
                    title='source' 
                    objects={sources}
                    onSubmit={addObject}
                    columns={getColumns(sources)}
                    overrideButtonStyle={{
                        title: 'Add source',
                    }}
                />
            </Placeholder>
        )
    }

    const getActions = (source) => [];

    return {
        title: 'Sources',
        getColumns,
        parentObjects: sources,
        getListItemProps,
        idField,
        saveObjects,
        deleteObject,
        addObject,
        getActions,
        getPlaceholder
    }
}

const hasMultiInputSourceSupport = (processors, hubs) => {
    return processors?.some(processor => processorSupportsMultiInput(processor.processorId, processors, hubs));
}

const sourceSupportsMultiInput = (source, processors, hubs) => {
    return processorSupportsMultiInput(source.processorId, processors, hubs);
}

const showMultiInputField = (source, processors, hubs) => {
    return (hasMultiInputSourceSupport(processors, hubs) && sourceSupportsMultiInput(source, processors, hubs))
}

const processorSupportsMultiInput = (processorId, processors, hubs) => {
    const processor = processors?.find(processor => processor.processorId === processorId);
    const hub = hubs?.find(hub => hub.hubId === processor?.hubId);
    return isHubVersionAtLeast(hub?.programVersion, FLEX_MATRIX_MINIMUM_HUB_VERSION);
}

const parseInputs = (value) => {
    // convert from comma separated string to array of numbers
    return value.split(',').map(input => parseInt(input.trim())).filter(input => !isNaN(input));
}

const formatInputs = (inputs) => {
    // convert from array of numbers to comma separated string
    return inputs?.join(', ') || '';
}

// Wrapper that preserves the raw typed text (commas, spaces, in-progress digits)
// while syncing the parsed number array up to the parent.
const InputsField = ({ inputs, setInputs, cellState, disabled }) => {
    const [text, setText] = useState(formatInputs(inputs));

    // Re-sync from props only when the parsed array actually differs from
    // the currently typed text, so we don't clobber the user's input.
    useEffect(() => {
        const formatted = formatInputs(inputs);
        if (formatInputs(parseInputs(text)) !== formatted) {
            setText(formatted);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputs]);

    return (
        <StringField
            value={text}
            setValue={(value) => {
                setText(value);
                setInputs(parseInputs(value));
            }}
            cellState={cellState}
            placeholder='1, 2'
            disabled={disabled}
        />
    );
}
